/**
 * MasterDataValuationFormDialog — Create/Edit `list_and_valo` (valuation view).
 * 1:1 port of Access `frmMdQryValuationAdd`. Compact Sales Contract style.
 *
 * Layout (Access): Code (row) → Description (full, multiline) →
 * Bean Size | Category → Ranked | Phase → Special Requirements → Note →
 * Description on Contract → ... Offshore → Description on Quality Control.
 * `code` là PK: required, disable khi edit. VBA: code trống → "Please type
 * Code!"; code trùng (create) → "Code is existed!" (BE raise → snackbar).
 * premium_vs_g2 / valuation_in_use là cột tính (Updates page) — KHÔNG sửa ở đây.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdValuationMutation,
  useUpdateMdValuationMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataValuation } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataValuation | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  code: string
  description: string
  bean_size: string
  category: string
  ranked: string
  phase: string
  special_requirements: string
  note: string
  description_on_contract: string
  description_on_contract_offshore: string
  quality_control_des: string
}

const EMPTY: FormState = {
  code: '',
  description: '',
  bean_size: '',
  category: '',
  ranked: '',
  phase: '',
  special_requirements: '',
  note: '',
  description_on_contract: '',
  description_on_contract_offshore: '',
  quality_control_des: '',
}

export default function MasterDataValuationFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createValuation, { isLoading: creating }] = useCreateMdValuationMutation()
  const [updateValuation, { isLoading: updating }] = useUpdateMdValuationMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        code: initialValue.code ?? '',
        description: initialValue.description ?? '',
        bean_size: initialValue.bean_size ?? '',
        category: initialValue.category ?? '',
        ranked: initialValue.ranked != null ? String(initialValue.ranked) : '',
        phase: initialValue.phase ?? '',
        special_requirements: initialValue.special_requirements ?? '',
        note: initialValue.note ?? '',
        description_on_contract: initialValue.description_on_contract ?? '',
        description_on_contract_offshore: initialValue.description_on_contract_offshore ?? '',
        quality_control_des: initialValue.quality_control_des ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.code.trim()
  const canSave = !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const toInt = (s: string): number | null => {
    const t = s.trim()
    if (t === '') return null
    const n = parseInt(t, 10)
    return Number.isNaN(n) ? null : n
  }

  const buildBody = () => ({
    description: form.description.trim() || null,
    bean_size: form.bean_size.trim() || null,
    category: form.category.trim() || null,
    ranked: toInt(form.ranked),
    phase: form.phase.trim() || null,
    special_requirements: form.special_requirements.trim() || null,
    note: form.note.trim() || null,
    description_on_contract: form.description_on_contract.trim() || null,
    description_on_contract_offshore: form.description_on_contract_offshore.trim() || null,
    quality_control_des: form.quality_control_des.trim() || null,
  })

  const handleSave = async () => {
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateValuation({ code: initialValue.code, body: buildBody() }).unwrap()
        snack.success('Valuation updated')
      } else {
        await createValuation({ code: form.code.trim(), ...buildBody() }).unwrap()
        snack.success('Valuation created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Valuation' : 'New Valuation'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Code cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Bean Size"
              size="small"
              fullWidth
              value={form.bean_size}
              onChange={(e) => set('bean_size', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Category"
              size="small"
              fullWidth
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ranked"
              size="small"
              fullWidth
              type="number"
              value={form.ranked}
              onChange={(e) => set('ranked', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5, inputMode: 'numeric', step: 1, style: { textAlign: 'right' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phase"
              size="small"
              fullWidth
              value={form.phase}
              onChange={(e) => set('phase', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 6 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Special Requirements"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.special_requirements}
              onChange={(e) => set('special_requirements', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 7 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 8 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description On Contract"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.description_on_contract}
              onChange={(e) => set('description_on_contract', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 9 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description On Contract Offshore"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.description_on_contract_offshore}
              onChange={(e) => set('description_on_contract_offshore', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 10 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description On Quality Control"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.quality_control_des}
              onChange={(e) => set('quality_control_des', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 11 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
